/**
 * "Learn how to prepare food safely" lessons for onboarding. Short, one screen each, with a 2–3 question check.
 * Content is condensed from docs/source. They prepare staff for, but do not replace, the state-approved
 * food handler course (COURSE_LINKS.foodHandlerProviders).
 */
import { courseLinksFor } from "./requirements.ts";
import { LEARN_UT } from "./states/ut/learn.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";

export interface Lesson {
  id: string;
  title: string;
  minutes: number;
  points: string[];
  quiz: { q: string; choices: string[]; answer: number }[];
  requirementIds: string[];
}


const WORDS: Record<UsState, StateWords<typeof LEARN_UT>> = perStateTable("LEARN", LEARN_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

/** The required food handler course for a state. A state with no verified rule says to ask the health department. */
export function requiredCourseFor(state?: UsState) {
  const w = words(state);
  return {
    title: w.courseTitle ?? "Food handler permit course",
    why: w.courseWhy ?? "Ask your local health department which food handler training and permit your state requires.",
    providersUrl: courseLinksFor(state)?.foodHandlerProviders.url ?? "",
    citation: w.courseCitation ?? "",
  };
}
/** Utah's course. Apps read the place's own rule (cardrule.ts cardRuleAt), never this (test/one-truth-lint.test.ts). */
export const REQUIRED_COURSE = requiredCourseFor("UT");

/** The lessons for a state (only the thermometer lesson names the state; Utah's is LESSONS). */
export function lessonsFor(state?: UsState): Lesson[] {
  const w = words(state);
  return [
  { id: "sick", title: "Don't work sick", minutes: 2, requirementIds: ["fc-employee-health"], points: [
      "Vomiting, diarrhea, jaundice, sore throat with fever, or a pus-filled sore means no food work. Tell the person in charge.",
      "After vomiting or diarrhea with no diagnosis, wait 24 hours symptom-free.",
      "Norovirus spreads before you feel sick and for weeks after. Soap and water, not sanitizer, removes it."],
    quiz: [{ q: "You had diarrhea last night and feel fine now. Can you work with food today?", choices: ["Yes", "No, wait 24 hours symptom-free and tell the person in charge"], answer: 1 }] },
  { id: "hands", title: "Wash your hands", minutes: 2, requirementIds: ["fc-handwashing", "fc-hygiene"], points: [
      "Warm water (100°F+), soap, scrub 10–15 seconds: palms, backs, between fingers, under nails, above the wrists. 20 seconds in all.",
      "Only at a handwashing sink, never the dish or prep sink.",
      "After the restroom, wash twice: there and again at the kitchen sink.",
      "Dry with a clean paper towel; turn the faucet off with it."],
    quiz: [{ q: "Where may you wash your hands?", choices: ["Any sink", "Only a handwashing sink"], answer: 1 }, { q: "Can hand sanitizer replace washing?", choices: ["Yes", "No, only after washing"], answer: 1 }] },
  { id: "barehand", title: "No bare hands on ready-to-eat food", minutes: 2, requirementIds: ["fc-bare-hand"], points: [
      "Buns, salads, cut fruit, cooked meat, pastries and ice: use tongs, deli tissue, waxed paper or gloves.",
      "Wash before gloving. New gloves for each task and between raw and ready-to-eat food.",
      "Never wash or reuse single-use gloves."],
    quiz: [{ q: "You handled raw chicken. Next you'll plate cooked chicken. What first?", choices: ["Keep the same gloves", "Remove gloves, wash hands, new gloves and clean tongs"], answer: 1 }] },
  { id: "temps", title: "Temperatures that matter", minutes: 3, requirementIds: ["fc-cooking", "fc-holding", "fc-reheating", "fc-cooling"], points: [
      "Danger zone is 41°F to 135°F. Keep hot food 135°F+ and cold food 41°F or below.",
      "Cook poultry to 165°F, burgers to 160°F, steaks, chops and fish to 145°F. Color is not a test.",
      "Cool 135→70°F in 2 hours, then to 41°F within 6 hours total. Reheat to 165°F within 2 hours, only once.",
      "Food out of temperature control is thrown out after 4 hours, or 1 hour above 90°F."],
    quiz: [{ q: "What's the cold-holding limit?", choices: ["45°F", "41°F or below"], answer: 1 }, { q: "Burgers are done when…", choices: ["No pink inside", "A thermometer reads 160°F"], answer: 1 }] },
  { id: "thermometer", title: "Use and calibrate a thermometer", minutes: 2, requirementIds: ["fc-thermometers"], points: [
      "Probe the thickest part; sanitize the probe between foods.",
      "Calibrate daily: a large glass of crushed ice and water must read 32°F.",
      ...(w.boilingPoint ? [w.boilingPoint] : [])],
    quiz: [{ q: w.calibrateQuestion ?? "How do you calibrate?", choices: ["Boiling water at 212°F", "Ice water at 32°F"], answer: 1 }] },
  { id: "storage", title: "Store, label and thaw", minutes: 3, requirementIds: ["fc-storage", "fc-date-marking", "fc-thawing"], points: [
      "Fridge top to bottom: ready-to-eat, fish, whole cuts, ground meat, poultry.",
      "Label everything: what it is, use-by date, how to store it. The day it's made is day 1; use within 7 days.",
      "Thaw in the fridge, under cold running water, in the microwave, or as part of cooking. Never on the counter.",
      "First in, first out."],
    quiz: [{ q: "Where does raw chicken go?", choices: ["Top shelf", "Bottom shelf"], answer: 1 }] },
  { id: "cross", title: "Cross-contamination and allergens", minutes: 2, requirementIds: ["fc-allergens", "fc-storage"], points: [
      "Separate boards and utensils for raw meat and ready-to-eat food; prepare produce first, raw meat last.",
      "Nine major allergens: milk, eggs, peanuts, tree nuts, soy, wheat, fish, shellfish, sesame. Label them; never guess.",
      "Heat and sanitizer don't remove allergens; washing does."],
    quiz: [{ q: "Does sanitizer remove allergens?", choices: ["Yes", "No, wash with soap and water"], answer: 1 }] },
  { id: "clean", title: "Clean, then sanitize", minutes: 2, requirementIds: ["fc-sanitizing", "fc-chemicals"], points: [
      "Scrape, wash (110°F), rinse (110°F), sanitize, air-dry. Never towel-dry.",
      "Chlorine 50–99 ppm for 10 seconds, or quat per label for 30 seconds. Test with a strip.",
      "Never mix chemicals. Store them below and away from food."],
    quiz: [{ q: "After sanitizing, you…", choices: ["Towel-dry", "Air-dry"], answer: 1 }] },
  { id: "report", title: "Report problems right away", minutes: 1, requirementIds: ["fc-pest-control", "fc-vomit-cleanup"], points: [
      "See a pest, a warm fridge, a spill, an injury or vomit? Tap Report in the app. Add a photo.",
      "If someone already reported it, tap \"Still there\" instead of filing again.",
      "Never spray pesticides or handle bats or bird droppings yourself."],
    quiz: [{ q: "You see mouse droppings in the pantry. What do you do?", choices: ["Sweep them up and move on", "Report it with a photo so the manager can act today"], answer: 1 }] },
  ];
}

/** Utah's lessons (its thermometer lesson names Utah). Apps read `lessonsFor(place's state)`, or LESSON_INDEX for ids and titles. */
export const LESSONS: Lesson[] = lessonsFor("UT");

/**
 * State-neutral: every state's lessons have the same ids, titles and minutes; only a lesson's points and quiz name a state
 * (the thermometer lesson's boiling point). Proven for all states by packages/core/test/one-truth.test.ts. For a list, a count,
 * a title or an id check; never for the lesson text itself (that is `lessonsFor(state)`).
 */
export const LESSON_INDEX: readonly { id: string; title: string; minutes: number }[] = LESSONS.map(({ id, title, minutes }) => ({ id, title, minutes }));
export const lessonTitle = (id?: string): string | undefined => LESSON_INDEX.find((l) => l.id === id)?.title;
